import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { DOCTORS, SPECIALTIES } from "@/lib/data";
import { Calendar, X } from "lucide-react";
import { useLang } from "@/lib/i18n";

export default function Doctors() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("specialty");
  const [active, setActive] = useState(
    initial && SPECIALTIES.includes(initial) ? initial : "All"
  );
  const { t } = useLang();
  const gridRef = useRef(null);

  // React to URL changes (e.g., back/forward, links from OPD page)
  useEffect(() => {
    const sp = params.get("specialty");
    if (sp && SPECIALTIES.includes(sp)) {
      setActive(sp);
      // Smooth scroll to the doctor grid so the filter result is visible
      setTimeout(() => {
        gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } else if (!sp) {
      setActive("All");
    }
  }, [params]);

  const onPick = (sp) => {
    setActive(sp);
    if (sp === "All") {
      params.delete("specialty");
    } else {
      params.set("specialty", sp);
    }
    setParams(params, { replace: true });
  };

  const list = active === "All" ? DOCTORS : DOCTORS.filter((d) => d.specialty === active);

  return (
    <div data-testid="doctors-page">
      <PageHero
        eyebrow={t("doctors.eyebrow")}
        title={t("doctors.title")}
        subtitle={t("doctors.subtitle")}
        crumbs={[{ label: t("nav.doctors") }]}
        image="https://images.pexels.com/photos/6129441/pexels-photo-6129441.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
      />

      <section className="py-12 sm:py-16" ref={gridRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-4 mb-6 border-b border-black/5" data-testid="specialty-filter">
            {SPECIALTIES.map((sp) => (
              <button
                key={sp}
                onClick={() => onPick(sp)}
                className={`whitespace-nowrap px-4 py-2 text-sm rounded-full font-medium transition-colors ${
                  active === sp ? "bg-deep text-white" : "bg-white text-deep border border-black/10 hover:border-brand"
                }`}
                data-testid={`filter-${sp.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {sp}
              </button>
            ))}
          </div>

          {/* Active filter chip */}
          {active !== "All" && (
            <div className="flex items-center gap-3 mb-6" data-testid="active-filter">
              <span className="text-xs tracking-[0.18em] uppercase text-ink-soft font-semibold">Showing</span>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand/10 text-brand text-sm font-semibold">
                {active} <span className="text-brand/60">·</span> {list.length}
                <button
                  onClick={() => onPick("All")}
                  className="ml-1 -mr-1 p-0.5 rounded-full hover:bg-brand/20"
                  aria-label="Clear filter"
                  data-testid="clear-filter"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            </div>
          )}

          {list.length === 0 ? (
            <div className="text-center text-ink-soft py-10">{t("doctors.empty")}</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {list.map((d) => (
                <div key={d.name} className="bg-white rounded-3xl overflow-hidden border border-black/5 shadow-soft hover:shadow-lifted transition-all group" data-testid={`doctor-${d.name.replace(/\s+/g, '-')}`}>
                  <div className="aspect-[4/5] overflow-hidden bg-brand/5">
                    <img src={d.image} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-gold font-bold mb-1">{d.specialty}</div>
                    <div className="font-display text-deep text-lg font-semibold leading-tight">{d.name}</div>
                    <div className="text-xs text-ink-soft italic mt-1">{d.qual}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
